import { useMemo } from 'react'

import { safeHref } from '@/shared/lib/url/safe-href'
import type { GamificationPageBlock } from '@/types/gamificationPageBlock'

import { getBlockRegistryEntry } from '../blocks/registry'
import { useHomeBlocks } from './useHomeBlocks'

export type HomeFeedMode = 'hub' | 'feed' | 'none'

/**
 * Replica, por `kind`, a checagem de conteúdo que cada componente addable de
 * `home/blocks/custom/CustomBlocks.tsx` já faz para retornar `null` — não
 * inventa uma regra própria de "vazio" para as duas não divergirem em
 * silêncio. Blocos fixos (`emptyPolicy: 'placeholder'`) sempre contam: eles
 * sempre renderizam algo, seja dado real ou `BlockEmptyState`.
 *
 * `useHomeBlocks()` não filtra por `block.active` hoje — filtramos aqui
 * explicitamente para essa função ser correta e testável isoladamente, sem
 * depender do snapshot da api já vir sem blocos inativos.
 */
export function isRenderableBlock(block: GamificationPageBlock): boolean {
  if (!block.active) return false

  const entry = getBlockRegistryEntry(block.kind)
  if (!entry) return false
  if (entry.emptyPolicy === 'placeholder') return true

  switch (block.kind) {
    case 'banner':
      return !!block.media?.file?.url
    case 'rich_text':
      return !!block.text_content
    case 'embed':
      return !!block.embed_code
    case 'cta':
      return !!block.title && !!safeHref(block.external_link)
    case 'section_title':
      return !!block.title
    default:
      return false
  }
}

export function resolveHomeFeedMode(blocks: GamificationPageBlock[]): HomeFeedMode {
  const renderable = blocks.filter(isRenderableBlock)
  const hasSocialFeed = renderable.some(b => b.kind === 'social_feed')
  if (!hasSocialFeed) return 'none'
  return renderable.length === 1 ? 'feed' : 'hub'
}

export function useHomeFeedMode(): HomeFeedMode {
  const { blocks } = useHomeBlocks()
  return useMemo(() => resolveHomeFeedMode(blocks), [blocks])
}
